import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, COLOR_SYSTEM, Text, Icon } from 'components/atoms';
import { StepData } from './index';

interface ExperimentStep2Props {
  data: StepData;
  onNext: (data: Partial<StepData>) => void;
  onBack: () => void;
}

const ExperimentStep2: React.FC<ExperimentStep2Props> = ({
  data,
  onNext,
  onBack,
}) => {
  const [selectedMode, setSelectedMode] = useState<'create' | 'evaluate' | 'reevaluate' | null>(
    data.modes?.createMode ? 'create' :
    data.modes?.evaluateMode ? 'evaluate' :
    data.modes?.reevaluateMode ? 'reevaluate' : null
  );
  
  const [error, setError] = useState('');
  
  const handleModeSelect = (mode: 'create' | 'evaluate' | 'reevaluate') => {
    setSelectedMode(mode);
    setError('');
  };
  
  const validate = () => {
    if (!selectedMode) {
      setError('실행 모드를 선택해야 합니다.');
      return false;
    }
    return true;
  };
  
  const handleNext = () => {
    if (!validate()) return;
    onNext({ 
      modes: {
        createMode: selectedMode === 'create',
        evaluateMode: selectedMode === 'evaluate',
        reevaluateMode: selectedMode === 'reevaluate',
      }
    });
  };
  
  return (
    <Container>
      <StepTitle>실행 모드 선택</StepTitle>
      <StepDescription>
        실행 모드를 선택하세요. 선택한 모드에 따라 필요한 설정이 달라집니다.
      </StepDescription>
      
      <ModesContainer>
        <ModeCard 
          $selected={selectedMode === 'create'}
          onClick={() => handleModeSelect('create')}
        >
          <ModeHeader>
            <ModeIcon>
              <Icon name="Plus" size={24} color={selectedMode === 'create' ? "Blue" : "Gray"} />
            </ModeIcon>
            <ModeTitle $selected={selectedMode === 'create'}>
              모드 1: AI Review 생성 + 평가
            </ModeTitle>
            <RadioButton $selected={selectedMode === 'create'}>
              {selectedMode === 'create' && <RadioDot />}
            </RadioButton>
          </ModeHeader>
          <ModeDescription>
            새로운 AI Review를 생성하고 Ground Truth와 비교하여 평가합니다.
            선택한 환자들에 대해 AI Review를 생성한 후 즉시 평가를 수행합니다.
          </ModeDescription>
        </ModeCard>
        
        <ModeCard 
          $selected={selectedMode === 'evaluate'}
          onClick={() => handleModeSelect('evaluate')}
        >
          <ModeHeader>
            <ModeIcon>
              <Icon name="Review Document" size={24} color={selectedMode === 'evaluate' ? "Blue" : "Gray"} />
            </ModeIcon>
            <ModeTitle $selected={selectedMode === 'evaluate'}>
              모드 2: 기존 AI Review 평가
            </ModeTitle>
            <RadioButton $selected={selectedMode === 'evaluate'}>
              {selectedMode === 'evaluate' && <RadioDot />}
            </RadioButton>
          </ModeHeader>
          <ModeDescription>
            이미 생성된 AI Review를 현재 Ground Truth와 비교하여 평가합니다.
            평가되지 않은 AI Review나 재평가가 필요한 경우에 사용합니다.
          </ModeDescription>
        </ModeCard>
        
        <ModeCard 
          $selected={selectedMode === 'reevaluate'}
          onClick={() => handleModeSelect('reevaluate')}
        >
          <ModeHeader>
            <ModeIcon>
              <Icon name="Refresh" size={24} color={selectedMode === 'reevaluate' ? "Blue" : "Gray"} />
            </ModeIcon>
            <ModeTitle $selected={selectedMode === 'reevaluate'}>
              모드 3: 기존 평가 재평가
            </ModeTitle>
            <RadioButton $selected={selectedMode === 'reevaluate'}>
              {selectedMode === 'reevaluate' && <RadioDot />}
            </RadioButton>
          </ModeHeader>
          <ModeDescription>
            이미 평가된 결과를 다시 평가합니다.
            평가 설정의 GT Snapshot 옵션에 따라 기존 GT 또는 현재 GT를 사용합니다.
          </ModeDescription>
        </ModeCard>
      </ModesContainer>
      
      {error && <ErrorText>{error}</ErrorText>}
      
      <ButtonGroup>
        <Button
          size="Medium"
          type="Text"
          variant="Ghost"
          color="Secondary"
          onClick={onBack}
        >
          이전
        </Button>
        <Button
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={handleNext}
        >
          다음
        </Button>
      </ButtonGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const StepDescription = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;
`;

const ModesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModeCard = styled.div<{ $selected: boolean }>`
  padding: 20px;
  border: 2px solid ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[500] : COLOR_SYSTEM.get("Gray")?.[200]
  };
  border-radius: 8px;
  background-color: ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[50] : 'white'
  };
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${({ $selected }) => 
      $selected ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[300]
    };
  }
`;

const ModeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ModeIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const ModeTitle = styled(Text)<{ $selected: boolean }>`
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[700] : COLOR_SYSTEM.get("Gray")?.[900]
  };
`;

const RadioButton = styled.div<{ $selected: boolean }>`
  width: 24px;
  height: 24px;
  border: 2px solid ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[500] : COLOR_SYSTEM.get("Gray")?.[300]
  };
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
`;

const RadioDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[500]};
`;

const ModeDescription = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;
  padding-left: 52px;
`;

const ErrorText = styled.span`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Red")?.[500]};
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

export default ExperimentStep2;